import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Infinity as InfinityIcon, FileText, Sparkles, CheckCircle2, LockOpen, ExternalLink } from "lucide-react";
import { usePro } from "@/context/ProContext";
import { PRO_FEATURES, PRO_PRICE, UPGRADE_URL } from "@/config/pricing";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const FEATURE_ICONS = { Infinity: InfinityIcon, FileText, Sparkles };

export default function Paywall() {
  const navigate = useNavigate();
  const { isPro, redeemCode, redeeming } = usePro();
  const [code, setCode] = useState("");

  const onGetPro = () => window.open(UPGRADE_URL, "_blank");

  const onRedeem = async () => {
    const ok = await redeemCode(code);
    if (ok) {
      toast.success("Pro unlocked 🎉", { description: "All Pro features are now active on this device." });
      setTimeout(() => navigate("/"), 700);
    } else {
      toast.error("Invalid code", { description: "That code didn't match. Double-check and try again." });
    }
  };

  return (
    <div className="mx-auto max-w-xl pb-16" data-testid="paywall-screen">
      <div className="relative rounded-b-2xl px-6 pb-8 pt-8 text-center" style={{ background: "linear-gradient(160deg,#4A7A59,#243B2C)" }}>
        <button
          data-testid="paywall-close"
          onClick={() => navigate("/")}
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-white transition-colors hover:bg-white/25"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <div className="mx-auto mb-3 mt-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15">
          <Sparkles size={28} className="text-white" />
        </div>
        <h1 className="font-display text-2xl font-bold text-white">CreditCost Pro</h1>
        <p className="mx-auto mt-1.5 max-w-sm text-sm leading-snug text-white/85">
          Unlock the full estimating toolkit for your business — one payment, yours forever.
        </p>
      </div>

      <div className="px-5 pt-6 sm:px-6">
        {isPro && (
          <div
            className="mb-6 flex items-center gap-2.5 rounded-xl bg-brand-tint p-4"
            data-testid="paywall-already-pro"
          >
            <CheckCircle2 size={22} className="text-brand" />
            <span className="font-display font-bold text-brand">You're a Pro member 🎉</span>
          </div>
        )}

        <div className="mb-7 space-y-4">
          {PRO_FEATURES.map((f) => {
            const Icon = FEATURE_ICONS[f.icon] || Sparkles;
            return (
              <div key={f.title} className="flex items-center gap-3.5">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-tint">
                  <Icon size={20} className="text-brand" />
                </div>
                <div>
                  <p className="font-display font-bold">{f.title}</p>
                  <p className="text-sm leading-snug text-muted-foreground">{f.sub}</p>
                </div>
              </div>
            );
          })}
        </div>

        {!isPro && (
          <>
            <p className="mb-2 font-display text-xs font-bold tracking-wider text-muted-foreground">
              GET LIFETIME PRO
            </p>
            <div className="rounded-xl border border-brand bg-surface-2 p-4" data-testid="lifetime-card">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-display font-bold">
                    Lifetime Pro <span className="text-sm font-bold text-brand">ONE-TIME</span>
                  </p>
                  <p className="text-sm text-muted-foreground">Pay once. Yours forever. No subscription.</p>
                </div>
                <span className="font-mono text-2xl font-bold text-brand tabular">{PRO_PRICE}</span>
              </div>
              <p className="mt-3 text-xs leading-snug text-muted-foreground">
                Secure checkout via Stripe. After paying, you'll receive an unlock code by email — enter it
                below to activate Pro on this device.
              </p>
            </div>

            <button
              data-testid="paywall-get-pro"
              onClick={onGetPro}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-4 font-display font-bold text-white transition-colors hover:bg-brand-dark"
            >
              Get Pro — {PRO_PRICE}
              <ExternalLink size={17} />
            </button>

            <p className="mb-2 mt-7 text-sm font-medium text-muted-foreground">
              Already purchased? Enter your code
            </p>
            <div className="flex gap-2">
              <Input
                data-testid="paywall-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && code.trim() && onRedeem()}
                placeholder="UNLOCK CODE"
                autoCapitalize="characters"
                autoCorrect="off"
                className="flex-1 bg-surface-2 border-border font-mono tracking-wider"
              />
              <button
                data-testid="paywall-redeem"
                onClick={onRedeem}
                disabled={!code.trim() || redeeming}
                className="flex items-center gap-1.5 rounded-md bg-foreground px-5 font-display font-bold text-background transition-opacity disabled:opacity-50"
              >
                {redeeming ? "…" : <><LockOpen size={16} /> Redeem</>}
              </button>
            </div>

            <p className="mt-6 text-center text-xs leading-snug text-muted-foreground">
              One-time purchase — pay once, no subscription. Secure checkout by Stripe. Pro unlocks unlimited
              projects, PDF &amp; CSV export, and removes the promo footer.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
